/**
 * Shared FS-mode PL amount computation used by single Current column and MULTI YOA columns.
 */
import type {AuditBuildCompanyFSTreeItems} from '@/services/auditService/auditCompanyFSCategoryService';
import {formatAmountDisplay, parseAmountDisplay} from '@/utils/dataUtils';
import {FS_CODE, findNodeByCodes} from '@/utils/fsCodes';
import {plComputeComputedAmounts, plIsProtectedParentHeader} from '@/pages/components/fs-report/FsFormula';
import {toNestedTree} from '@/utils/fsDerived';
import {
    resolvePlCotInputMode,
    shouldFreezeCosWhenIntroducingBsInventory,
} from '@/utils/plCostOfSalesVisibility';

export type PlCotInputMode = 'COS' | 'PUR';

export type PlCotFsIds = {
    oiFsId?: number;
    purFsId?: number;
    ciFsId?: number;
    cosFsId?: number;
    afterTaxFsId?: number;
};

export function resolvePlCotFsIds(fsTree: any[] | null | undefined): PlCotFsIds {
    if (!fsTree || fsTree.length === 0) return {};
    const oiNode = findNodeByCodes(fsTree as any[], FS_CODE.PL.OPENING_INVENTORIES);
    const purNode = findNodeByCodes(fsTree as any[], FS_CODE.PL.PURCHASES);
    const ciNode = findNodeByCodes(fsTree as any[], FS_CODE.PL.CLOSING_INVENTORIES);
    const cosNode = findNodeByCodes(fsTree as any[], FS_CODE.PL.COST_OF_SALES);
    const afterTaxNode = findNodeByCodes(fsTree as any[], FS_CODE.PL.AFTER_TAX);
    return {
        oiFsId: oiNode?.fsId != null ? Number(oiNode.fsId) : undefined,
        purFsId: purNode?.fsId != null ? Number(purNode.fsId) : undefined,
        ciFsId: ciNode?.fsId != null ? Number(ciNode.fsId) : undefined,
        cosFsId: cosNode?.fsId != null ? Number(cosNode.fsId) : undefined,
        afterTaxFsId: afterTaxNode?.fsId != null ? Number(afterTaxNode.fsId) : undefined,
    };
}

/**
 * Data/save map: keep only the active COT input (COS or PUR); formula side stays 0.
 * Render still uses computePlFsAmountsFromDisplays / lastYear computed maps.
 */
export function zeroPlCotFormulaSideInDataMap(
    map: Record<number, string>,
    cotInputMode: PlCotInputMode,
    cotFsIds: PlCotFsIds,
): Record<number, string> {
    const next = {...map};
    if (cotInputMode === 'COS' && cotFsIds.purFsId != null) {
        next[cotFsIds.purFsId] = '0';
    } else if (cotInputMode === 'PUR' && cotFsIds.cosFsId != null) {
        next[cotFsIds.cosFsId] = '0';
    }
    return next;
}

/**
 * 公式 PUR = COS − OI − CI（OI=BS LY Inventories，CI=−BS Current Inventories）。
 * BS 两年都有值且 COS=0 时：PUR = Current − LY。
 */
export function resolvePlCotPurFormulaAmount(oiAmt: number, ciAmt: number, cosAmt: number): number {
    return (Number(cosAmt) || 0) - (Number(oiAmt) || 0) - (Number(ciAmt) || 0);
}

/** PUR 可编下限：不得小于公式算出的 PUR。 */
export function clampPlCotPurToFormulaMin(
    pur: number,
    oiAmt: number,
    ciAmt: number,
    cosAmt: number,
): number {
    const floor = resolvePlCotPurFormulaAmount(oiAmt, ciAmt, cosAmt);
    return Math.max(Number(pur) || 0, floor);
}

/**
 * COT mode switch: rebuild a formula-consistent COS/PUR pair from the *previous*
 * editable input, then zero the formula side in the data map (save path).
 *
 * COS→PUR：把公式 PUR（含 BS OI/CI）写入数据层；返回 purCosFloor 供后续夹紧。
 */
export function applyPlCotInputModeSwitch(args: {
    nextMode: PlCotInputMode;
    /** Mode before switch; selects which display is the trusted input. */
    prevMode?: PlCotInputMode;
    cotFsIds: PlCotFsIds;
    displays: Record<number, string>;
    dataMap: Record<number, string>;
    computed: Record<number, number>;
    /** Opening inventories (OI) used by COS = OI + PUR + CI */
    oiAmt?: number;
    /** Closing inventories leaf (CI), typically −BS current inventories */
    ciAmt?: number;
}): {
    displays: Record<number, string>;
    dataMap: Record<number, string>;
    /** 切到 PUR 后夹紧用的 COS 基数（切换前 COS 录入值；无则 0） */
    purCosFloor: number | null;
} {
    const {nextMode, cotFsIds, computed} = args;
    const prevMode = args.prevMode ?? (nextMode === 'COS' ? 'PUR' : 'COS');
    const oiAmt = Number(args.oiAmt) || 0;
    const ciAmt = Number(args.ciAmt) || 0;
    const displays = {...args.displays};
    let dataMap = {...args.dataMap};
    const cosId = cotFsIds.cosFsId;
    const purId = cotFsIds.purFsId;

    const readAmount = (id: number | undefined, preferComputed: boolean): number => {
        if (id == null || !Number.isFinite(id)) return 0;
        if (preferComputed && Object.prototype.hasOwnProperty.call(computed, id)) {
            return Number(computed[id]) || 0;
        }
        return parseAmountDisplay(displays[id] ?? '0');
    };

    const cosInput = (() => {
        const d = readAmount(cosId, false);
        if (Math.abs(d) >= 0.01) return d;
        return readAmount(cosId, true);
    })();

    // Prefer previous editable *display* (sync does not overwrite editable leaves).
    let pur = readAmount(purId, false);
    let cos = readAmount(cosId, false);

    if (prevMode === 'PUR' && Math.abs(pur) >= 0.01) {
        cos = oiAmt + pur + ciAmt;
    } else if (prevMode === 'COS' && Math.abs(cos) >= 0.01) {
        pur = cos - oiAmt - ciAmt;
    } else if (Math.abs(cos) >= 0.01 || Math.abs(readAmount(cosId, true)) >= 0.01) {
        cos = Math.abs(cos) >= 0.01 ? cos : readAmount(cosId, true);
        pur = cos - oiAmt - ciAmt;
    } else {
        // COS≈0：公式 PUR = −OI − CI（BS Current − BS LY）写入展示/后续数据层
        pur = Math.abs(pur) >= 0.01 ? pur : readAmount(purId, true);
        const formulaPur = resolvePlCotPurFormulaAmount(oiAmt, ciAmt, 0);
        if (Math.abs(pur) < 0.01 && Math.abs(formulaPur) >= 0.01) pur = formulaPur;
        cos = oiAmt + pur + ciAmt;
    }

    let purCosFloor: number | null = null;
    if (nextMode === 'PUR') {
        // 下限用切换前 COS 录入（COS 模式）或 0，再与 OI/CI 合成公式 PUR
        const cosForFloor = prevMode === 'COS' ? cosInput : 0;
        pur = clampPlCotPurToFormulaMin(pur, oiAmt, ciAmt, cosForFloor);
        cos = oiAmt + pur + ciAmt;
        purCosFloor = cosForFloor;
    }

    if (cosId != null && Number.isFinite(cosId)) {
        const seed = String(cos);
        displays[cosId] = seed;
        dataMap[cosId] = seed;
    }
    if (purId != null && Number.isFinite(purId)) {
        const seed = String(pur);
        displays[purId] = seed;
        dataMap[purId] = seed;
    }

    dataMap = zeroPlCotFormulaSideInDataMap(dataMap, nextMode, cotFsIds);
    return {displays, dataMap, purCosFloor};
}

export function computePlFsAmountsFromDisplays(args: {
    fsTree: any[] | null | undefined;
    amountDisplays: Record<number, string>;
    bsInventoriesCurrentAmount: number;
    bsInventoriesLastYearAmount: number;
    cotInputMode: PlCotInputMode;
    /** Optional: skip repeated findNodeByCodes when caller already resolved ids. */
    cotFsIds?: PlCotFsIds;
    /**
     * First-year BS inventory: freeze COS to the PUR base (PUR mode only).
     * Mutable ref so the locked base survives recompute without React state loops.
     */
    frozenCosBaseRef?: {current: number | null};
    /**
     * After user toggles COS↔PUR, skip freeze so switch-seeded formula pairs
     * are not re-inflated on the next PUR pass.
     */
    cotFreezeSuppressedRef?: {current: boolean};
    /**
     * PUR 可编时公式下限用的 COS 基数（切换前 COS 录入；null 则按 0，即 PUR≥−OI−CI）。
     */
    purCosFloorRef?: {current: number | null};
}): Record<number, number> {
    const {
        fsTree,
        amountDisplays,
        bsInventoriesCurrentAmount,
        bsInventoriesLastYearAmount,
        cotInputMode,
        frozenCosBaseRef,
        cotFreezeSuppressedRef,
        purCosFloorRef,
    } = args;
    if (!fsTree || fsTree.length === 0) return {};

    const ids = args.cotFsIds ?? resolvePlCotFsIds(fsTree);
    const oiFsId = ids.oiFsId;
    const purFsId = ids.purFsId;
    const ciFsId = ids.ciFsId;
    const cosFsId = ids.cosFsId;

    const cotOverrides: Record<number, number> = {};

    const oiAmt = bsInventoriesLastYearAmount;
    const ciAmt = -bsInventoriesCurrentAmount;
    if (oiFsId) cotOverrides[oiFsId] = oiAmt;
    if (ciFsId) cotOverrides[ciFsId] = ciAmt;

    const purchasesRaw = purFsId ? parseAmountDisplay(amountDisplays[purFsId] ?? '0') : 0;
    const costOnCos = cosFsId ? parseAmountDisplay(amountDisplays[cosFsId] ?? '0') : 0;
    // PUR 可编：不得低于公式 PUR = COS_floor − OI − CI
    const cosForPurFloor = purCosFloorRef?.current != null ? Number(purCosFloorRef.current) || 0 : 0;
    const purchases = cotInputMode === 'PUR'
        ? clampPlCotPurToFormulaMin(purchasesRaw, oiAmt, ciAmt, cosForPurFloor)
        : purchasesRaw;
    const freezeCosForNewInventory = !cotFreezeSuppressedRef?.current
        && shouldFreezeCosWhenIntroducingBsInventory({
            bsInventoriesCurrent: bsInventoriesCurrentAmount,
            bsInventoriesLastYear: bsInventoriesLastYearAmount,
            openingInventories: oiFsId != null ? (cotOverrides[oiFsId] ?? 0) : 0,
        });

    // 仅 PUR 模式可冻结：此时 Purchases 为可编辑叶子，sync 不会覆盖 amountDisplays[PUR]；
    // 若在 COS 模式写回 PUR，会与输入互读导致 Maximum update depth。
    // PUR 保持用户录入；只锁 COS=PUR 基数（勿写 PUR=COS−OI−CI，否则切换时会叠 |CI|）。
    if (freezeCosForNewInventory && cotInputMode === 'PUR'
        && cosFsId && purFsId && oiFsId != null && ciFsId != null && frozenCosBaseRef) {
        if (frozenCosBaseRef.current == null && Math.abs(purchases) >= 0.01) {
            frozenCosBaseRef.current = purchases;
        }
        const costBase = frozenCosBaseRef.current;
        if (costBase != null && Math.abs(costBase) >= 0.01) {
            cotOverrides[cosFsId] = costBase;
            cotOverrides[purFsId] = purchases;
        } else if (Math.abs(purchases) < 0.01 && Math.abs(costOnCos) >= 0.01) {
            cotOverrides[cosFsId] = costOnCos;
            cotOverrides[purFsId] = costOnCos - (cotOverrides[oiFsId] ?? 0) - (cotOverrides[ciFsId] ?? 0);
        }
    } else {
        if (frozenCosBaseRef) frozenCosBaseRef.current = null;
        if (cotInputMode === 'COS') {
            // COS 可编：OI/CI 跟 BS；PUR = COS − OI − CI（展示层；落库 map 仍清 PUR=0）
            const costOfSales = cosFsId ? parseAmountDisplay(amountDisplays[cosFsId] ?? '0') : 0;
            if (cosFsId) cotOverrides[cosFsId] = costOfSales;
            if (purFsId && oiFsId && ciFsId) {
                cotOverrides[purFsId] = costOfSales - cotOverrides[oiFsId] - cotOverrides[ciFsId];
            }
        } else {
            // PUR 可编：COS = OI + PUR + CI；OI/CI 仍跟 BS
            const purFromCos = costOnCos - oiAmt - ciAmt;
            // OI 曾为 0 时留下的脏值：PUR ≈ COS − CI（= COS+存货），OI 就绪后应改回 COS−OI−CI
            const purIfOiWasZero = costOnCos - ciAmt;
            const missingOiArtifact = Math.abs(oiAmt) >= 0.01
                && Math.abs(costOnCos) >= 0.01
                && Math.abs(purchases - purIfOiWasZero) < 0.02
                && Math.abs(purchases - purFromCos) >= 0.02;

            if ((Math.abs(purchases) < 0.01 && Math.abs(costOnCos) >= 0.01) || missingOiArtifact) {
                if (cosFsId) cotOverrides[cosFsId] = costOnCos;
                if (purFsId && oiFsId && ciFsId) {
                    cotOverrides[purFsId] = clampPlCotPurToFormulaMin(
                        purFromCos,
                        oiAmt,
                        ciAmt,
                        cosForPurFloor,
                    );
                }
            } else {
                if (purFsId) cotOverrides[purFsId] = purchases;
                if (cosFsId && oiFsId && ciFsId) {
                    cotOverrides[cosFsId] = oiAmt + purchases + ciAmt;
                }
            }
        }
    }

    if (typeof localStorage !== 'undefined' && localStorage.getItem('DEBUG_FS_BALANCE') === '1') {
        // eslint-disable-next-line no-console
        console.log('[FS_BALANCE][PL-COT]', {
            cotInputMode,
            freezeCosForNewInventory,
            frozenCosBase: frozenCosBaseRef?.current ?? null,
            bsInventoriesCurrentAmount,
            bsInventoriesLastYearAmount,
            purchases,
            costOnCos,
            oiAmt: oiFsId != null ? cotOverrides[oiFsId] : undefined,
            ciAmt: ciFsId != null ? cotOverrides[ciFsId] : undefined,
            overrides: {
                oi: oiFsId != null ? cotOverrides[oiFsId] : undefined,
                pur: purFsId != null ? cotOverrides[purFsId] : undefined,
                ci: ciFsId != null ? cotOverrides[ciFsId] : undefined,
                cos: cosFsId != null ? cotOverrides[cosFsId] : undefined,
            },
        });
    }

    const leafGetter = (node: AuditBuildCompanyFSTreeItems): number => {
        if (plIsProtectedParentHeader(node)) return 0;
        const fsId = Number((node as any).fsId);
        if (Object.prototype.hasOwnProperty.call(cotOverrides, fsId)) {
            return cotOverrides[fsId];
        }
        return parseAmountDisplay(amountDisplays[fsId] || '');
    };

    const computed = plComputeComputedAmounts((fsTree || []) as any, leafGetter);
    for (const [fsId, val] of Object.entries(cotOverrides)) {
        computed[Number(fsId)] = val;
    }
    return computed;
}

export function sumAmountDisplayMaps(
    maps: Record<string, Record<number, string>> | null | undefined,
): Record<number, string> {
    const out: Record<number, string> = {};
    if (!maps) return out;
    for (const m of Object.values(maps)) {
        for (const [k, v] of Object.entries(m || {})) {
            const id = Number(k);
            if (Number.isNaN(id)) continue;
            const prev = parseAmountDisplay(out[id] ?? '0');
            const cur = parseAmountDisplay(v ?? '0');
            out[id] = String(prev + cur);
        }
    }
    return out;
}

/** Aggregate per-YOA computed number maps (Current = Σ YOA). */
export function sumComputedAmountMaps(
    maps: Record<string, Record<number, number>> | null | undefined,
): Record<number, number> {
    const out: Record<number, number> = {};
    if (!maps) return out;
    for (const m of Object.values(maps)) {
        for (const [k, v] of Object.entries(m || {})) {
            const id = Number(k);
            if (Number.isNaN(id)) continue;
            out[id] = (out[id] ?? 0) + (Number(v) || 0);
        }
    }
    return out;
}

function inventoriesAmountFromBs(
    bsTree: any[] | null | undefined,
    bsValues: Record<number, string> | null | undefined,
): number {
    const inv = findNodeByCodes((bsTree || []) as any[], FS_CODE.BS.INVENTORIES);
    if (!inv?.fsId) return 0;
    return parseAmountDisplay((bsValues || {})[inv.fsId] ?? '0');
}

/**
 * Last-year PL COT: CI = -BS inventories last year；OI 优先 PL lastYear map，
 * 缺失时回退为上年存货结余（避免 OI=0 时 PUR = COS + 存货）。
 */
export function computePlLastYearAmountsFromDisplays(args: {
    fsTree: any[] | null | undefined;
    lastYearAmounts: Record<number, string>;
    bsInventoriesLastYearAmount: number;
    cotInputMode: PlCotInputMode;
    cotFsIds?: PlCotFsIds;
}): Record<number, number> {
    const {fsTree, lastYearAmounts, bsInventoriesLastYearAmount, cotInputMode} = args;
    if (!fsTree || fsTree.length === 0) return {};

    const ids = args.cotFsIds ?? resolvePlCotFsIds(fsTree);
    const oiFsId = ids.oiFsId;
    const purFsId = ids.purFsId;
    const ciFsId = ids.ciFsId;
    const cosFsId = ids.cosFsId;

    const cotOverrides: Record<number, number> = {};
    // 上年期末存货 → Closing；上年期初优先用 PL map。
    // GL→FS 不写 PL-COT-OI 时 map 为 0，若仍用 CI=-存货、OI=0 反推 PUR，
    // 会得到 PUR = COS - CI = COS + 存货（例如 597245+400000=997245）。
    // 期初未知时与当期一致：用上年存货结余作期初，使 OI+CI=0，PUR=COS。
    let openingInventories = oiFsId ? parseAmountDisplay(lastYearAmounts[oiFsId] ?? '0') : 0;
    if (Math.abs(openingInventories) < 0.01 && Math.abs(bsInventoriesLastYearAmount) >= 0.01) {
        openingInventories = bsInventoriesLastYearAmount;
    }
    if (oiFsId) cotOverrides[oiFsId] = openingInventories;
    if (ciFsId) cotOverrides[ciFsId] = -bsInventoriesLastYearAmount;

    if (cotInputMode === 'COS') {
        const costOfSalesLastYear = cosFsId ? parseAmountDisplay(lastYearAmounts[cosFsId] ?? '0') : 0;
        if (cosFsId) cotOverrides[cosFsId] = costOfSalesLastYear;
        if (purFsId && oiFsId && ciFsId) {
            cotOverrides[purFsId] = costOfSalesLastYear - cotOverrides[ciFsId] - cotOverrides[oiFsId];
        }
    } else {
        const purchasesLastYear = purFsId ? parseAmountDisplay(lastYearAmounts[purFsId] ?? '0') : 0;
        const costOnCosLastYear = cosFsId ? parseAmountDisplay(lastYearAmounts[cosFsId] ?? '0') : 0;
        if (Math.abs(purchasesLastYear) < 0.01 && Math.abs(costOnCosLastYear) >= 0.01) {
            if (cosFsId) cotOverrides[cosFsId] = costOnCosLastYear;
            if (purFsId && oiFsId && ciFsId) {
                cotOverrides[purFsId] = costOnCosLastYear - cotOverrides[ciFsId] - cotOverrides[oiFsId];
            }
        } else {
            if (purFsId) cotOverrides[purFsId] = purchasesLastYear;
            if (cosFsId && oiFsId && ciFsId) {
                cotOverrides[cosFsId] = cotOverrides[oiFsId] + purchasesLastYear + cotOverrides[ciFsId];
            }
        }
    }

    const leafGetter = (node: AuditBuildCompanyFSTreeItems): number => {
        if (plIsProtectedParentHeader(node)) return 0;
        const fsId = Number((node as any).fsId);
        if (Object.prototype.hasOwnProperty.call(cotOverrides, fsId)) {
            return cotOverrides[fsId];
        }
        return parseAmountDisplay(lastYearAmounts[fsId] || '');
    };

    const computed = plComputeComputedAmounts((fsTree || []) as any, leafGetter);
    for (const [fsId, val] of Object.entries(cotOverrides)) {
        computed[Number(fsId)] = val;
    }
    return computed;
}

/**
 * Compute PL-PAT (after tax) for FS validation without mounting ProfitLoss.
 * MULTI: This Year (BS-EQ-LFP) = Σ PL-PAT across YOA columns.
 * Last Year: plLastYearValues only (comparative). Do not inject prior-YOA PAT —
 * BS-EQ-AL Current = LY(AL)+LY(LFP)+LY(DIV); injecting would double-count with Σ LFP.
 * SINGLE: unchanged (single current map + lastYear map).
 */
export function computeFsPlAfterTaxFromMaps(args: {
    plTree: any[] | null | undefined;
    bsTree: any[] | null | undefined;
    plValues: Record<number, string>;
    plValuesByYoa?: Record<string, Record<number, string>> | null;
    plLastYearValues: Record<number, string>;
    bsValues: Record<number, string>;
    bsLastYearValues: Record<number, string>;
    cotInputMode: PlCotInputMode;
}): {afterTax: number; afterTaxLastYear: number} {
    const {
        plTree,
        bsTree,
        plValues,
        plValuesByYoa,
        plLastYearValues,
        bsValues,
        bsLastYearValues,
        cotInputMode,
    } = args;

    const cotFsIds = resolvePlCotFsIds(plTree);
    const afterTaxFsId = cotFsIds.afterTaxFsId;
    if (!afterTaxFsId) {
        return {afterTax: 0, afterTaxLastYear: 0};
    }

    const bsInvCurrent = inventoriesAmountFromBs(bsTree, bsValues);
    const bsInvLastYear = inventoriesAmountFromBs(bsTree, bsLastYearValues);

    const yoaKeys = Object.keys(plValuesByYoa || {}).filter(
        (k) => k && Object.keys((plValuesByYoa as any)[k] || {}).length > 0,
    );
    const isMulti = yoaKeys.length >= 2;

    const computeYoaAfterTax = (yoaMap: Record<number, string>) => {
        const computed = computePlFsAmountsFromDisplays({
            fsTree: plTree,
            amountDisplays: yoaMap || {},
            bsInventoriesCurrentAmount: bsInvCurrent,
            bsInventoriesLastYearAmount: bsInvLastYear,
            cotInputMode,
            cotFsIds,
        });
        return computed[afterTaxFsId] ?? 0;
    };

    let afterTax = 0;
    if (isMulti) {
        for (const yoa of yoaKeys) {
            afterTax += computeYoaAfterTax((plValuesByYoa as any)[yoa] || {});
        }
    } else {
        afterTax = computeYoaAfterTax(plValues || {});
    }

    // 上年 COT 可能与本年不同（本年 COS、上年仅 PUR）——勿用本年模式算 LY after-tax
    const cotInputModeLastYear = resolvePlCotInputMode(null, {
        plTree,
        bsTree,
        plValuesMap: plLastYearValues || {},
        bsValuesMap: bsLastYearValues || {},
        bsLastYearMap: bsLastYearValues || {},
    }) as PlCotInputMode;
    const lastYearComputed = computePlLastYearAmountsFromDisplays({
        fsTree: plTree,
        lastYearAmounts: plLastYearValues || {},
        bsInventoriesLastYearAmount: bsInvLastYear,
        cotInputMode: cotInputModeLastYear,
        cotFsIds,
    });
    const afterTaxLastYear = lastYearComputed[afterTaxFsId] ?? 0;

    return {afterTax, afterTaxLastYear};
}

// ─── Init snapshot + idle warmup (FsDetail precomputes while BS is visible) ───

export type PlFsInitSnapshot = {
    fsTree: any[];
    amountDisplays: Record<number, string>;
    amountDisplaysByYoa: Record<string, Record<number, string>>;
    lastYearAmounts: Record<number, string>;
    expanded: Record<number, boolean>;
    computed: Record<number, number>;
    computedByYoa: Record<string, Record<number, number>>;
    lastYearComputed: Record<number, number>;
    cotFsIds: PlCotFsIds;
    /** 按上年金额推断；与本年 cotInputMode 可能不同（本年 COS / 上年 PUR） */
    cotInputModeLastYear: PlCotInputMode;
    signature: string;
};

function formatMap(src: Record<number | string, any> | null | undefined): Record<number, string> {
    const formatted: Record<number, string> = {};
    for (const [k, v] of Object.entries(src || {})) {
        formatted[Number(k)] = formatAmountDisplay(v as any);
    }
    return formatted;
}

function collectExpandedParents(nodes: any[]): Record<number, boolean> {
    const expandedNodes: Record<number, boolean> = {};
    const markExpanded = (list: any[]) => {
        for (const node of list || []) {
            const hasKids = Array.isArray(node?.children) && node.children.length > 0;
            if (hasKids) {
                expandedNodes[Number(node.fsId)] = true;
                markExpanded(node.children);
            }
        }
    };
    markExpanded(nodes);
    return expandedNodes;
}

/** Count all nodes (not only roots) so warmup signature changes when custom leaves appear. */
export function countFsTreeNodes(nodes: any[] | null | undefined): number {
    let n = 0;
    const walk = (arr: any[]) => {
        for (const it of arr || []) {
            n += 1;
            if (Array.isArray(it?.children) && it.children.length > 0) walk(it.children);
        }
    };
    walk(nodes || []);
    return n;
}

function collectLeafAmountDisplays(tree: any[]): Record<number, string> {
    const init: Record<number, string> = {};
    const visit = (nodes: any[]) => {
        for (const item of nodes || []) {
            const leaf = !Array.isArray(item?.children) || item.children.length === 0;
            if (leaf && !plIsProtectedParentHeader(item)) {
                // getFs / queryAdjustmentList put money on openingAmount, not amount
                init[item.fsId] = formatAmountDisplay(item.openingAmount ?? item.amount);
            }
            if (Array.isArray(item.children) && item.children.length > 0) visit(item.children);
        }
    };
    visit(tree);
    return init;
}

export function buildPlFsWarmupSignature(args: {
    annualId: number;
    fsYoaColumns: string[] | null | undefined;
    plTreeLen: number;
    plValues: Record<number, string> | null | undefined;
    plValuesByYoa: Record<string, Record<number, string>> | null | undefined;
    plLastYearValues: Record<number, string> | null | undefined;
    bsValues: Record<number, string> | null | undefined;
    bsLastYearValues: Record<number, string> | null | undefined;
    cotInputMode: PlCotInputMode;
}): string {
    const yoaPart = Array.isArray(args.fsYoaColumns) && args.fsYoaColumns.length >= 2
        ? args.fsYoaColumns.join(',')
        : '';
    const yoaBuckets = Object.keys(args.plValuesByYoa || {})
        .filter((k) => k && Object.keys((args.plValuesByYoa as any)[k] || {}).length > 0)
        .sort()
        .map((k) => `${k}:${Object.keys((args.plValuesByYoa as any)[k]).length}`)
        .join('|');
    return [
        args.annualId,
        yoaPart,
        args.plTreeLen,
        Object.keys(args.plValues || {}).length,
        yoaBuckets,
        Object.keys(args.plLastYearValues || {}).length,
        Object.keys(args.bsValues || {}).length,
        Object.keys(args.bsLastYearValues || {}).length,
        args.cotInputMode,
    ].join('::');
}

export function buildPlFsInitSnapshot(args: {
    annualId: number;
    plTreeFlat: any[] | null | undefined;
    plValues: Record<number, string> | null | undefined;
    plValuesByYoa: Record<string, Record<number, string>> | null | undefined;
    plLastYearValues: Record<number, string> | null | undefined;
    bsTree: any[] | null | undefined;
    bsValues: Record<number, string> | null | undefined;
    bsLastYearValues: Record<number, string> | null | undefined;
    cotInputMode: PlCotInputMode;
    fsYoaColumns: string[] | null | undefined;
}): PlFsInitSnapshot | null {
    const flat = args.plTreeFlat;
    if (!flat || !Array.isArray(flat) || flat.length === 0) return null;

    const signature = buildPlFsWarmupSignature({
        annualId: args.annualId,
        fsYoaColumns: args.fsYoaColumns,
        plTreeLen: countFsTreeNodes(flat),
        plValues: args.plValues,
        plValuesByYoa: args.plValuesByYoa,
        plLastYearValues: args.plLastYearValues,
        bsValues: args.bsValues,
        bsLastYearValues: args.bsLastYearValues,
        cotInputMode: args.cotInputMode,
    });

    const tree = toNestedTree(flat as any[]) as any[];
    const expanded = collectExpandedParents(tree);

    const storeByYoa = args.plValuesByYoa || {};
    const fsYoaColumns = Array.isArray(args.fsYoaColumns) && args.fsYoaColumns.length >= 2
        ? args.fsYoaColumns
        : null;
    const isMulti = !!fsYoaColumns;

    let amountDisplaysByYoa: Record<string, Record<number, string>> = {};
    let amountDisplays: Record<number, string>;

    if (isMulti && fsYoaColumns) {
        const hasAny = Object.keys(storeByYoa).some((k) => Object.keys(storeByYoa[k] || {}).length > 0);
        if (hasAny) {
            for (const yoa of fsYoaColumns) {
                const src = storeByYoa[yoa] || storeByYoa[''] || {};
                amountDisplaysByYoa[yoa] = formatMap(src);
            }
            if (storeByYoa[''] && Object.keys(amountDisplaysByYoa[fsYoaColumns[0]] || {}).length === 0) {
                amountDisplaysByYoa[fsYoaColumns[0]] = formatMap(storeByYoa['']);
            }
        } else {
            const base = args.plValues && Object.keys(args.plValues).length > 0
                ? args.plValues
                : collectLeafAmountDisplays(tree);
            for (const yoa of fsYoaColumns) {
                amountDisplaysByYoa[yoa] = {};
            }
            amountDisplaysByYoa[fsYoaColumns[0]] = formatMap(base);
        }
        amountDisplays = sumAmountDisplayMaps(amountDisplaysByYoa);
    } else if (args.plValues && Object.keys(args.plValues).length > 0) {
        amountDisplays = formatMap(args.plValues);
    } else {
        amountDisplays = collectLeafAmountDisplays(tree);
    }

    // 必须在 pre-bake 前按「原始」上年金额推断 LY 模式。
    // 若用本年 COS 去算上年，会把上年 PUR 冲成 0 再写入 lastYearAmounts（MOON Cap 差就出在这）。
    const lastYearAmountsRaw = args.plLastYearValues && Object.keys(args.plLastYearValues).length > 0
        ? formatMap(args.plLastYearValues)
        : {};
    const cotInputModeLastYear = resolvePlCotInputMode(null, {
        plTree: tree,
        bsTree: args.bsTree,
        plValuesMap: lastYearAmountsRaw,
        bsValuesMap: args.bsLastYearValues || {},
        bsLastYearMap: args.bsLastYearValues || {},
    }) as PlCotInputMode;
    const lastYearAmounts: Record<number, string> = {...lastYearAmountsRaw};

    const cotFsIds = resolvePlCotFsIds(tree);
    const bsInvCurrent = inventoriesAmountFromBs(args.bsTree, args.bsValues);
    const bsInvLastYear = inventoriesAmountFromBs(args.bsTree, args.bsLastYearValues);

    let computedByYoa: Record<string, Record<number, number>> = {};
    let computed: Record<number, number>;

    if (isMulti && fsYoaColumns) {
        for (const yoa of fsYoaColumns) {
            computedByYoa[yoa] = computePlFsAmountsFromDisplays({
                fsTree: tree,
                amountDisplays: amountDisplaysByYoa[yoa] || {},
                bsInventoriesCurrentAmount: bsInvCurrent,
                bsInventoriesLastYearAmount: bsInvLastYear,
                cotInputMode: args.cotInputMode,
                cotFsIds,
            });
        }
        // Aggregate Current from summed displays (not Σ of per-YOA computed —
        // COT / inventory overrides are not linear across YOA columns).
        computed = computePlFsAmountsFromDisplays({
            fsTree: tree,
            amountDisplays,
            bsInventoriesCurrentAmount: bsInvCurrent,
            bsInventoriesLastYearAmount: bsInvLastYear,
            cotInputMode: args.cotInputMode,
            cotFsIds,
        });
    } else {
        computed = computePlFsAmountsFromDisplays({
            fsTree: tree,
            amountDisplays,
            bsInventoriesCurrentAmount: bsInvCurrent,
            bsInventoriesLastYearAmount: bsInvLastYear,
            cotInputMode: args.cotInputMode,
            cotFsIds,
        });
    }

    const lastYearComputed = computePlLastYearAmountsFromDisplays({
        fsTree: tree,
        lastYearAmounts: lastYearAmountsRaw,
        bsInventoriesLastYearAmount: bsInvLastYear,
        cotInputMode: cotInputModeLastYear,
        cotFsIds,
    });

    // Pre-bake COT leaf amounts into display maps so ProfitLoss mount sync does not
    // clone amountDisplays and force a second full-table paint.
    const cotIdList = [cotFsIds.oiFsId, cotFsIds.purFsId, cotFsIds.ciFsId, cotFsIds.cosFsId]
        .filter((id): id is number => id != null && Number.isFinite(id));
    for (const fsId of cotIdList) {
        if (Object.prototype.hasOwnProperty.call(computed, fsId)) {
            amountDisplays[fsId] = String(computed[fsId]);
        }
        if (Object.prototype.hasOwnProperty.call(lastYearComputed, fsId)) {
            lastYearAmounts[fsId] = String(lastYearComputed[fsId]);
        }
    }
    if (isMulti && fsYoaColumns) {
        for (const yoa of fsYoaColumns) {
            const col = amountDisplaysByYoa[yoa] || (amountDisplaysByYoa[yoa] = {});
            const yComputed = computedByYoa[yoa] || {};
            for (const fsId of cotIdList) {
                if (Object.prototype.hasOwnProperty.call(yComputed, fsId)) {
                    col[fsId] = String(yComputed[fsId]);
                }
            }
        }
    }

    return {
        fsTree: tree,
        amountDisplays,
        amountDisplaysByYoa,
        lastYearAmounts,
        expanded,
        computed,
        computedByYoa,
        lastYearComputed,
        cotFsIds,
        cotInputModeLastYear,
        signature,
    };
}

type WarmupEntry = {signature: string; snapshot: PlFsInitSnapshot};
let plFsWarmupEntry: WarmupEntry | null = null;
let plFsWarmupTimer: ReturnType<typeof setTimeout> | null = null;
let plFsWarmupIdleId: number | null = null;

export function peekPlFsWarmup(signature: string): PlFsInitSnapshot | null {
    if (!plFsWarmupEntry || plFsWarmupEntry.signature !== signature) return null;
    return plFsWarmupEntry.snapshot;
}

/** Consume matching warmup (one-shot) so mount does not rebuild. */
export function consumePlFsWarmup(signature: string): PlFsInitSnapshot | null {
    const snap = peekPlFsWarmup(signature);
    if (snap) plFsWarmupEntry = null;
    return snap;
}

export function setPlFsWarmup(snapshot: PlFsInitSnapshot): void {
    plFsWarmupEntry = {signature: snapshot.signature, snapshot};
}

/** Cancel pending idle/timer without dropping an already-built cache. */
export function cancelPendingPlFsWarmup(): void {
    if (plFsWarmupTimer != null) {
        clearTimeout(plFsWarmupTimer);
        plFsWarmupTimer = null;
    }
    if (plFsWarmupIdleId != null) {
        if (typeof cancelIdleCallback !== 'undefined') {
            cancelIdleCallback(plFsWarmupIdleId);
        }
        plFsWarmupIdleId = null;
    }
}

export function clearPlFsWarmup(): void {
    cancelPendingPlFsWarmup();
    plFsWarmupEntry = null;
}

/**
 * Schedule idle precompute of PL FS init snapshot (call from FsDetail while BS is visible).
 * Covers SINGLE Current column and MULTI YOA columns. Does not mount ProfitLoss;
 * click path can consume the cache.
 * Prefer delayMs=0 → requestIdleCallback; use delayMs>0 only for explicit deferral.
 */
export function schedulePlFsWarmup(build: () => PlFsInitSnapshot | null, delayMs = 0): void {
    cancelPendingPlFsWarmup();
    const run = () => {
        plFsWarmupTimer = null;
        plFsWarmupIdleId = null;
        try {
            const snap = build();
            if (snap) setPlFsWarmup(snap);
        } catch {
            // ignore warmup failures
        }
    };
    if (typeof requestIdleCallback !== 'undefined' && delayMs <= 0) {
        plFsWarmupIdleId = requestIdleCallback(() => run(), {timeout: 800}) as unknown as number;
        return;
    }
    plFsWarmupTimer = setTimeout(run, Math.max(0, delayMs));
}

/** Build snapshot immediately into cache (late YOA / PL already open). */
export function runPlFsWarmupNow(build: () => PlFsInitSnapshot | null): void {
    cancelPendingPlFsWarmup();
    try {
        const snap = build();
        if (snap) setPlFsWarmup(snap);
    } catch {
        // ignore
    }
}
